import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = axios.create({ baseURL: `${BACKEND_URL}/api` });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('cf_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('cf_token');
      localStorage.removeItem('cf_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: (username, password) => API.post('/auth/login', { username, password }),
};

export const sites = {
  list: () => API.get('/sites'),
  create: (data, superPassword) => API.post('/sites', { ...data, super_password: superPassword }),
  remove: (id, superPassword) => API.request({ method: 'DELETE', url: `/sites/${id}`, data: { super_password: superPassword } }),
  reseed: (superPassword) => API.post('/sites/reseed', { super_password: superPassword }),
};

export const templates = {
  list: () => API.get('/templates'),
  update: (id, variants) => API.put(`/templates/${id}`, { variants }),
  reseed: () => API.post('/templates/reseed'),
};

export const generate = {
  prefill: (seed) => API.post(`/generate/prefill?seed=${seed}`),
  bookmarklet: (seed, apiUrl, siteIds, sessionToken) =>
    API.post('/generate/bookmarklet', { seed, api_url: apiUrl, site_ids: siteIds, session_token: sessionToken }),
};

export const sessions = {
  create: (sites) => API.post('/sessions', { sites }),
  createPublic: (sites, consent_given, consent_text) => 
    axios.post(`${BACKEND_URL}/api/public/session`, { sites, consent_given, consent_text }),
  get: (token) => API.get(`/sessions/${token}`),
};

export const publicApi = {
  prefill: (seed) => axios.get(`${BACKEND_URL}/api/generate/prefill?seed=${seed}`),
  session: (sites, consentGiven = false, consentText = "", userName = "", userEmail = "", userPhone = "") =>
    axios.post(`${BACKEND_URL}/api/public/session`, { sites, consent_given: consentGiven, consent_text: consentText, user_name: userName, user_email: userEmail, user_phone: userPhone }),
  bookmarklet: (seed, apiUrl, sessionToken) =>
    axios.post(`${BACKEND_URL}/api/public/bookmarklet`, { seed, api_url: apiUrl, session_token: sessionToken }),
  loaderBookmarklet: (sessionToken, apiUrl, seed = 42) =>
    axios.post(`${BACKEND_URL}/api/public/loader-bookmarklet`, { session_token: sessionToken, api_url: apiUrl, seed }),
  progress: (token) => axios.get(`${BACKEND_URL}/api/public/progress/${token}`),
  enhance: (sessionToken, siteIndex, fieldKey, originalText, tone, personalContext) =>
    axios.post(`${BACKEND_URL}/api/public/enhance`, {
      session_token: sessionToken, site_index: siteIndex, field_key: fieldKey,
      original_text: originalText, tone, personal_context: personalContext,
    }),
  enhanceAll: (sessionToken, tone, personalContext) =>
    axios.post(`${BACKEND_URL}/api/public/enhance-all`, {
      session_token: sessionToken, tone, personal_context: personalContext,
    }),
  publicStats: () => axios.get(`${BACKEND_URL}/api/public/stats`),
};

export const submissions = {
  list: () => API.get('/admin/submissions'),
  delete: (token) => API.delete(`/admin/submissions/${token}`),
  export: () => API.get('/admin/submissions/export'),
};

export const stats = {
  get: () => API.get('/stats'),
  public: () => axios.get(`${BACKEND_URL}/api/public/stats`),
};

export const admin = {
  submissions: () => API.get('/admin/submissions'),
  deleteSubmission: (token) => API.delete(`/admin/submissions/${token}`),
  exportSubmissions: () => API.get('/admin/submissions/export'),
  siteStats: () => API.get('/admin/site-stats'),
  // Session management
  searchSessions: (email) => API.get(`/admin/sessions/search?email=${encodeURIComponent(email)}`),
  resetSession: (token, reason) => API.post(`/admin/sessions/${token}/reset`, { reason }),
  getSessionResetAudit: () => API.get('/admin/session-reset-audit'),
  fixConfirmedIncomplete: () => API.post('/admin/fix-confirmed-incomplete'),
};

export const volunteer = {
  listConsents: () => API.get('/volunteer/consents'),
  adminListConsents: () => API.get('/admin/volunteer-consents'),
  claimConsent: (recordId) => API.post(`/volunteer/consents/${recordId}/claim`),
  updateConsent: (recordId, data) => API.patch(`/admin/volunteer-consents/${recordId}`, data),
  markProcessed: (recordId) => API.patch(`/admin/volunteer-consents/${recordId}/processed`),
  recommendDeletion: (recordId, reason) => API.post(`/volunteer/consents/${recordId}/recommend-deletion?reason=${encodeURIComponent(reason)}`),
  deleteConsent: (recordId) => API.delete(`/admin/volunteer-consents/${recordId}`),
  cancelDeletion: (recordId) => API.post(`/admin/volunteer-consents/${recordId}/cancel-deletion`),
  getDeletionAuditLog: () => API.get('/admin/deletion-audit-log'),
};

export { BACKEND_URL };
export default API;
